//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Close, Copy } from '@carbon/icons-react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import TenantDataService from '../../utils/TenantDataService';
import { useConfig } from '../../Config';
import { createSecureLogger } from '../../utils/securityUtils';

const logger = createSecureLogger('CreateTokenModal');

interface CreateTokenModalProps {
  isOpen: boolean;
  tenantDataService: TenantDataService;
  onClose: () => void;
  /** Called after the caller's token state changes (mint), so callers can refresh. */
  onTokenChanged?: () => void;
}

type ModalMode = 'authorize' | 'loading' | 'token' | 'error';

const CreateTokenModal: React.FC<CreateTokenModalProps> = ({
  isOpen,
  tenantDataService,
  onClose,
  onTokenChanged,
}) => {
  const { t } = useTranslation();
  const { oidc } = useConfig();
  const [mode, setMode] = useState<ModalMode>('authorize');
  const [token, setToken] = useState('');
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Holds the expected state param so we can validate the popup response.
  const pendingState = useRef<string | null>(null);
  // Tracks the active listener cleanup so retries tear down the previous registration.
  const cleanupRef = useRef<(() => void) | undefined>(undefined);
  // Polls for the user closing/cancelling the popup so we don't spin forever.
  const popupPollRef = useRef<number | undefined>(undefined);

  const mint = useCallback(
    async (authCode: string, redirectUri: string) => {
      setBusy(true);
      setError(null);
      try {
        const result = await tenantDataService.mintMyToken(
          authCode,
          redirectUri,
        );
        setToken(result.token);
        setMode('token');
        setCopied(false);
        onTokenChanged?.();
      } catch (err: any) {
        setError(err.message || t('token.errors.createFailed'));
        setMode('error');
      } finally {
        setBusy(false);
      }
    },
    [tenantDataService, onTokenChanged, t],
  );

  const openConsentPopup = useCallback(
    (isRetry = false) => {
      // Tear down any prior listener registration before setting up new ones.
      cleanupRef.current?.();
      cleanupRef.current = undefined;

      const { authorization, mintingInteractiveClientId, mintingRedirectUri } =
        oidc;
      if (
        !authorization ||
        !mintingInteractiveClientId ||
        !mintingRedirectUri
      ) {
        setError(t('token.errors.mintingNotConfigured'));
        setMode('error');
        return;
      }

      // Reset state when retrying
      if (isRetry) {
        setMode('loading');
        setError(null);
      }

      const state = crypto.randomUUID();
      pendingState.current = state;

      const params = new URLSearchParams({
        response_type: 'code',
        client_id: mintingInteractiveClientId,
        scope: 'openid offline_access',
        redirect_uri: mintingRedirectUri,
        state,
        prompt: 'login', // force fresh session isolated from main app's session
      });
      const popupUrl = `${authorization}?${params.toString()}`;
      // noopener intentionally omitted: Chrome returns null from window.open when it's set
      // (even on success), which would make the !popup check below always report "blocked".
      // Safe without it: target is our own mintingRedirectUri, and we only talk back via
      // BroadcastChannel, never window.opener.
      const popup = window.open(
        popupUrl,
        'token-mint',
        'popup,width=600,height=700',
      );

      if (!popup) {
        setError(t('token.errors.popupBlocked'));
        setMode('error');
        return;
      }

      const stopPolling = () => {
        if (popupPollRef.current !== undefined) {
          window.clearInterval(popupPollRef.current);
          popupPollRef.current = undefined;
        }
      };

      // If the user closes the popup or cancels consent, no message ever arrives
      // to move us out of 'loading'; detect the closed window and surface Retry.
      popupPollRef.current = window.setInterval(() => {
        if (popup.closed) {
          stopPolling();
          cleanupRef.current?.();
          cleanupRef.current = undefined;
          setError(t('token.errors.popupClosed'));
          setMode('error');
        }
      }, 500);

      // Feature-detect BroadcastChannel and use exactly one delivery channel.
      // BroadcastChannel is preferred (leaves no persistent trace); localStorage is the fallback.
      const useBroadcastChannel = typeof BroadcastChannel !== 'undefined';

      const handleStorage = (event: StorageEvent) => {
        if (event.key !== 'token-mint-code' || !event.newValue) return;
        window.removeEventListener('storage', handleStorage);
        let data: { code?: string; state?: string } = {};
        try {
          data = JSON.parse(event.newValue);
        } catch {
          return;
        }
        localStorage.removeItem('token-mint-code');
        const { code, state: returnedState } = data;
        if (!code) return;
        stopPolling();
        if (returnedState !== pendingState.current) {
          setError(t('token.errors.stateMismatch'));
          setMode('error');
          return;
        }
        pendingState.current = null;
        mint(code, mintingRedirectUri);
      };

      let bc: BroadcastChannel | null = null;

      if (useBroadcastChannel) {
        // BroadcastChannel survives COOP header navigation where window.opener is nulled.
        bc = new BroadcastChannel('token-mint');
        logger.log('BroadcastChannel created, waiting for message...');
        bc.onmessage = (event: MessageEvent) => {
          logger.log('BroadcastChannel message received');
          const { type, code, state: returnedState } = event.data ?? {};
          // Guard BEFORE teardown - any early return should not tear down the listeners
          if (type !== 'TOKEN_MINT_CODE' || !code) {
            logger.warn('Invalid message type or missing code, ignoring');
            return;
          }

          // Now safe to tear down
          bc?.close();
          stopPolling();
          if (returnedState !== pendingState.current) {
            logger.error('State mismatch');
            setError(t('token.errors.stateMismatch'));
            setMode('error');
            return;
          }
          pendingState.current = null;
          logger.log('Calling mint with code');
          mint(code, mintingRedirectUri);
        };
      } else {
        // Fallback to localStorage when BroadcastChannel is unavailable
        logger.log('BroadcastChannel not available, using localStorage');
        window.addEventListener('storage', handleStorage);
      }

      const cleanup = () => {
        bc?.close();
        window.removeEventListener('storage', handleStorage);
        pendingState.current = null;
        stopPolling();
      };
      cleanupRef.current = cleanup;
      return cleanup;
    },
    [oidc, mint, t],
  );

  // Reset to authorize prompt whenever the modal is (re-)opened.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setMode('authorize');
    setToken('');
    setCopied(false);
    setError(null);
    return () => {
      cleanupRef.current?.();
      cleanupRef.current = undefined;
    };
  }, [isOpen]);

  // Must be called directly from a click handler so browsers treat it as a user gesture.
  const handleAuthorize = () => {
    setMode('loading');
    openConsentPopup();
  };

  const handleCopy = async () => {
    if (!token) {
      return;
    }
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t('token.errors.copyFailed'));
    }
  };

  const handleClose = () => {
    if (busy) {
      return;
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        data-testid="create-token-modal"
        className="max-w-2xl p-0 dark:bg-[#262626]"
      >
        {/* Header */}
        <DialogTitle className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('token.create.title')}
          </span>
          <button
            type="button"
            onClick={handleClose}
            aria-label={t('token.close')}
            className="rounded p-1 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
            data-testid="create-token-modal-close"
          >
            <Close size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </DialogTitle>

        {/* Content */}
        <div className="p-6">
          {mode === 'authorize' && (
            <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              {t('token.create.authorizePrompt')}
            </p>
          )}

          {mode === 'loading' && (
            <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              {busy ? t('token.create.generating') : t('token.loading')}
            </p>
          )}

          {mode === 'error' && (
            <div
              role="alert"
              className="rounded-md bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/30 dark:text-red-300"
            >
              {error}
            </div>
          )}

          {mode === 'token' && (
            <>
              <p className="mb-6 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                {t('token.create.autoGeneratedPrefix')}{' '}
                <span className="font-semibold">{t('token.tokenLabel')}</span>{' '}
                {t('token.create.autoGeneratedSuffix')}
              </p>

              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t('token.tokenLabel')}
                </h3>
                <button
                  type="button"
                  onClick={handleCopy}
                  title={t('token.copy')}
                  aria-label={t('token.copy')}
                  className="rounded p-1 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                  data-testid="create-token-modal-copy-icon"
                >
                  <Copy
                    size={16}
                    className="text-gray-500 dark:text-gray-400"
                  />
                </button>
              </div>

              <div
                className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800"
                data-testid="create-token-modal-token-value"
              >
                <p className="break-all font-mono text-sm text-blue-600 dark:text-blue-400">
                  {token}
                </p>
              </div>

              <div className="mb-2 rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
                <p className="text-xs text-yellow-800 dark:text-yellow-300">
                  <span className="font-semibold">
                    {t('token.create.importantLabel')}
                  </span>{' '}
                  {t('token.create.importantBody')}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={busy}
            className="px-6"
            data-testid="create-token-modal-cancel"
          >
            {t('token.cancel')}
          </Button>

          {mode === 'token' && (
            <Button
              onClick={handleCopy}
              className="px-6"
              data-testid="create-token-modal-copy"
            >
              {copied ? t('token.copied') : t('token.copy')}
            </Button>
          )}

          {mode === 'authorize' && (
            <Button
              onClick={handleAuthorize}
              className="px-6"
              data-testid="create-token-modal-authorize"
            >
              {t('token.create.authorize')}
            </Button>
          )}

          {mode === 'error' && (
            <Button
              onClick={() => openConsentPopup(true)}
              disabled={busy}
              className="px-6"
              data-testid="create-token-modal-retry"
            >
              {t('token.retry')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTokenModal;
